import { type CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { type Subject } from 'rxjs';

import { type ScrollOffsetAdapter } from '../scroll-offset-adapter/scroll-offset-adapter';

export class BlazeFixedSizeStrategy {
  private itemSize: number;
  private minBufferPx: number;
  private maxBufferPx: number;

  constructor(
    private readonly scrolledIndexChange$$: Subject<number>,
    private readonly scrollAdapter: ScrollOffsetAdapter,
    itemSize: number,
    minBufferPx: number,
    maxBufferPx: number,
  ) {
    this.itemSize = itemSize;
    this.minBufferPx = minBufferPx;
    this.maxBufferPx = maxBufferPx;
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.updateTotalContentSize(viewport);
    this.updateRenderedRange(viewport);
  }

  detach(): void {
    /* empty */
  }

  updateItemAndBufferSize(itemSize: number, minBufferPx: number, maxBufferPx: number): void {
    this.itemSize = itemSize;
    this.minBufferPx = minBufferPx;
    this.maxBufferPx = Math.max(minBufferPx, maxBufferPx);
  }

  onDataLengthChanged(viewport: CdkVirtualScrollViewport): void {
    this.updateTotalContentSize(viewport);
    this.updateRenderedRange(viewport);
  }

  scrollToIndex(viewport: CdkVirtualScrollViewport, index: number, behavior: ScrollBehavior): void {
    viewport.scrollToOffset(index * this.itemSize, behavior);
  }

  updateRenderedRange(viewport: CdkVirtualScrollViewport): void {
    const renderedRange = viewport.getRenderedRange();
    const newRange = { start: renderedRange.start, end: renderedRange.end };
    const viewportSize = viewport.getViewportSize();
    const dataLength = viewport.getDataLength();
    let scrollOffset = this.scrollAdapter.measureScrollOffset(viewport);

    // Prevent NaN as result when dividing by zero.
    let firstVisibleIndex = this.itemSize > 0 ? scrollOffset / this.itemSize : 0;

    // If user scrolls to the bottom of the list and data changes to a smaller list
    if (newRange.end > dataLength) {
      // We have to recalculate the first visible index based on new data length and viewport size.
      const maxVisibleItems = Math.ceil(viewportSize / this.itemSize);
      const newVisibleIndex = Math.max(
        0,
        Math.min(firstVisibleIndex, dataLength - maxVisibleItems),
      );

      // If first visible index changed we must update scroll offset to handle start/end buffers
      // Current range must also be adjusted to cover the new position (bottom of new list).
      if (firstVisibleIndex !== newVisibleIndex) {
        firstVisibleIndex = newVisibleIndex;
        scrollOffset = newVisibleIndex * this.itemSize;
        newRange.start = Math.floor(firstVisibleIndex);
      }

      newRange.end = Math.max(0, Math.min(dataLength, newRange.start + maxVisibleItems));
    }

    const startBuffer = scrollOffset - newRange.start * this.itemSize;
    if (startBuffer < this.minBufferPx && newRange.start !== 0) {
      const expandStart = Math.ceil((this.maxBufferPx - startBuffer) / this.itemSize);
      newRange.start = Math.max(0, newRange.start - expandStart);
      newRange.end = Math.min(
        dataLength,
        Math.ceil(firstVisibleIndex + (viewportSize + this.minBufferPx) / this.itemSize),
      );
    } else {
      const endBuffer = newRange.end * this.itemSize - (scrollOffset + viewportSize);
      if (endBuffer < this.minBufferPx && newRange.end !== dataLength) {
        const expandEnd = Math.ceil((this.maxBufferPx - endBuffer) / this.itemSize);
        if (expandEnd > 0) {
          newRange.end = Math.min(dataLength, newRange.end + expandEnd);
          newRange.start = Math.max(
            0,
            Math.floor(firstVisibleIndex - this.minBufferPx / this.itemSize),
          );
        }
      }
    }

    viewport.setRenderedRange(newRange);
    viewport.setRenderedContentOffset(this.itemSize * newRange.start);
    this.scrolledIndexChange$$.next(Math.floor(firstVisibleIndex));
  }

  updateTotalContentSize(viewport: CdkVirtualScrollViewport): void {
    viewport.setTotalContentSize(viewport.getDataLength() * this.itemSize);
  }
}
